import useEditLineItem from "@babackapp/private-api-client/client/use-edit-line-item";
import type {
	ProductVariantDto,
	ReturnRequestDto,
	ReturnRequestLineItemDto,
	UpdateLineItemNewVariantDto,
} from "@babackapp/private-api-client/types";
import {
	newVariantRequestFromLineItem,
	newVariantRequestFromVariant,
} from "@/lib/exchange/new-variant-request";
import { cloneDeep } from "lodash";
import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useState,
} from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import { useSetLineItemIsLoading } from "../../utils/line-items-loading-state-context";

type ResolutionsType =
	| "refund"
	| "exchange"
	| "exchange-credit"
	| "discount"
	| "new-articles"
	| null
	| undefined;

type RefundLineItemsContextType = {
	items: ReturnRequestLineItemDto[];
	setItems: (items: ReturnRequestLineItemDto[]) => void;
};

const RefundLineItemsContext = createContext<RefundLineItemsContextType>({
	items: [],
	setItems: () => {},
});

export function useRefundLineItems() {
	const { items, setItems } = useContext(RefundLineItemsContext);

	return {
		items,
		setItems,
	};
}

export function useRefundLineItem(lineItem: ReturnRequestLineItemDto) {
	const { items, setItems } = useRefundLineItems();
	const { data: shop } = useCurrentShopSuspense();
	const currentReturnRequest = useCurrentReturnRequest();
	const setLineItemLoading = useSetLineItemIsLoading();

	const { mutateAsync: editLineItem } = useEditLineItem({
		shopId: shop.id,
		returnRequestId: currentReturnRequest.id,
		onSuccess: (returnRequest: ReturnRequestDto) => {
			setItems(returnRequest.line_items);
		},
	});

	const item = items.find((li) => li.line_item_id === lineItem.line_item_id);

	if (!item) {
		throw new Error(`Line item with id ${lineItem.line_item_id} not found`);
	}

	const setItem = (id: string, newItem: ReturnRequestLineItemDto) => {
		const nextItems = [...items];
		const index = nextItems.findIndex((i) => i.line_item_id === id);
		nextItems[index] = newItem;
		setItems(nextItems);
	};

	/**
	 * Updates the new variant of the draft line item.
	 * This should only be available when the line item resolution
	 * is exchange
	 */
	const updateVariant = async (
		variant: ProductVariantDto,
		productTitle?: string | null,
	) => {
		setLineItemLoading(item.line_item_id, true);
		editLineItem({
			lineItemId: item.line_item_id,
			requestBody: {
				new_variant: newVariantRequestFromVariant(variant, productTitle),
			},
		}).finally(() => setLineItemLoading(item.line_item_id, false));
	};

	/**
	 * Update the draft line items's resolution.
	 * Note, if the resolution is not exchange,
	 * we should set the new_variant to null
	 * because we can only set one if the resolution is "exchange"
	 */
	const updateResolution = (resolution: ResolutionsType) => {
		/**
		 * If we want an exchange,m and no new_variant has been apoplied to
		 * the line item, we create a new variant with the current variant
		 *
		 * Example: From Tshirt Black L -> Tshirt Black L by default
		 *
		 * So if the merchant wants to exchange the product and
		 * does not select a new variant, shopify dont throw an
		 * error because we are sending one by default
		 */
		let newVariant: UpdateLineItemNewVariantDto | undefined;
		if (!lineItem.new_variant && resolution === "exchange") {
			newVariant = newVariantRequestFromLineItem(lineItem);
		}

		setLineItemLoading(item.line_item_id, true);
		editLineItem({
			lineItemId: item.line_item_id,
			requestBody: {
				resolution,
				new_variant: newVariant,
			},
		}).finally(() => setLineItemLoading(item.line_item_id, false));
	};

	return {
		item,
		setItem,
		updateVariant,
		updateResolution,
	};
}

/**
 * TODO: We kept the original name Refund*, but it should be renamed to something
 * more generic .. because the resolution can be exchange, discount, etc.
 *
 *  items: ReturnRequestLineItem[]
 *  setItems: (items: ReturnRequestLineItem[]) => void
 */

export function RefundLineItemsProvider({ children }: PropsWithChildren) {
	const currentReturnRequest = useCurrentReturnRequest();
	const lineItems = currentReturnRequest?.line_items
		? cloneDeep(currentReturnRequest.line_items)
		: [];

	const [items, setItems] = useState<ReturnRequestLineItemDto[]>(lineItems);

	useEffect(() => {
		setItems(currentReturnRequest.line_items);
	}, [currentReturnRequest]);

	return (
		<RefundLineItemsContext.Provider
			value={{
				items,
				setItems,
			}}
		>
			{children}
		</RefundLineItemsContext.Provider>
	);
}
